import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon, LogOut, User, ShieldAlert } from 'lucide-react';
import { Button } from './Button';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from './ThemeContext';
import { useTranslation } from './I18nContext';
import { useAuth } from './AuthContext';
import { LanguageSwitcher } from './LanguageSwitcher';

export const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useTranslation();
  const { user, isAdmin, signOut } = useAuth();

  const NAV_LINKS = [
    { label: t('nav.infrastructure'), path: '/services/infrastructure' },
    { label: t('nav.ai'), path: '/services/ai' },
    { label: t('nav.web'), path: '/services/web' },
    { label: t('nav.referenzen'), path: '/referenzen' },
    ...(language === 'de' ? [{ label: t('nav.blog'), path: '/blog' }] : []),
    { label: t('nav.about'), path: '/about' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <nav
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[94%] md:w-full md:max-w-7xl rounded-2xl transition-all duration-500 ease-out ${isScrolled || isMobileMenuOpen
          ? 'bg-white/60 dark:bg-[#020617]/60 backdrop-blur-2xl backdrop-saturate-150 border border-white/40 dark:border-white/10 shadow-2xl shadow-black/10 dark:shadow-black/40 py-3'
          : 'bg-white/30 dark:bg-[#020617]/20 backdrop-blur-md border border-white/10 py-5'
        }`}
    >
      <div className="px-6 flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center group">
          <img
            src="/Grafx/henselx_web_primary.png"
            alt="HenselX Logo"
            className={`h-8 w-auto transition-opacity duration-300 ${theme === 'dark' ? 'hidden' : 'block'}`}
          />
          <img
            src="/Grafx/henselx_web_inverted.png"
            alt="HenselX Logo"
            className={`h-8 w-auto transition-opacity duration-300 ${theme === 'dark' ? 'block' : 'hidden'}`}
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-all hover:text-[#0ea5e9] ${location.pathname === link.path
                  ? 'text-[#0ea5e9]'
                  : 'text-slate-800 dark:text-slate-200 hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]'
                }`}
            >
              {link.label}
            </Link>
          ))}
          {user && (
            <Link
              to="/dashboard"
              className={`text-sm font-medium transition-all hover:text-[#0ea5e9] ${location.pathname === '/dashboard'
                  ? 'text-[#0ea5e9]'
                  : 'text-slate-800 dark:text-slate-200'
                }`}
            >
              {t('nav.dashboard')}
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/admin"
              className={`text-sm font-bold transition-all hover:text-[#0ea5e9] flex items-center gap-1 ${location.pathname === '/admin'
                  ? 'text-[#0ea5e9]'
                  : 'text-slate-800 dark:text-slate-200'
                }`}
            >
              <ShieldAlert className="w-3 h-3" /> Admin
            </Link>
          )}
        </div>

        {/* CTA & Tools */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
            aria-label="Toggle Theme"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          <div className="w-px h-6 bg-slate-200 dark:bg-white/10 mx-1" />

          {user ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleLogout}
                className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-red-500 transition-colors px-2 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.logout')}
              </button>
              <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                <User className="w-4 h-4 text-slate-500" />
              </div>
            </div>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-[#0ea5e9] transition-colors px-2">
                {t('nav.login')}
              </Link>
              <Link to="/contact">
                <Button className="shadow-lg shadow-[#0ea5e9]/20">{t('nav.startProject')}</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <div className="flex items-center gap-4 md:hidden">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button
            className="text-slate-900 dark:text-white p-1"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden rounded-b-2xl"
          >
            <div className="flex flex-col p-6 space-y-4 border-t border-slate-200/20 dark:border-white/10 mt-2 bg-white/40 dark:bg-black/20 backdrop-blur-xl">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-lg font-medium text-slate-800 dark:text-slate-200 hover:text-[#0ea5e9]"
                >
                  {link.label}
                </Link>
              ))}
              {user && (
                <Link to="/dashboard" className="text-lg font-medium text-slate-800 dark:text-slate-200 hover:text-[#0ea5e9]">
                  {t('nav.dashboard')}
                </Link>
              )}
              {isAdmin && (
                <Link to="/admin" className="text-lg font-bold text-slate-800 dark:text-slate-200 hover:text-[#0ea5e9] flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4" /> Admin Console
                </Link>
              )}
              <hr className="border-slate-200/20 dark:border-white/10" />
              <div className="flex items-center justify-between py-2">
                <span className="text-slate-500 text-sm">Language</span>
                <LanguageSwitcher />
              </div>

              {user ? (
                <button onClick={handleLogout} className="text-lg font-medium text-left text-red-500 hover:text-red-400">
                  {t('nav.logout')}
                </button>
              ) : (
                <>
                  <Link to="/login" className="text-lg font-medium text-slate-800 dark:text-slate-200 hover:text-[#0ea5e9]">
                    {t('nav.login')}
                  </Link>
                  <Link to="/contact">
                    <Button className="w-full justify-center">{t('nav.startProject')}</Button>
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};